import { CustomFile } from "../../../Helpers/file";
import { currentViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { uuid } from "../../../../../Common/UUID";
import { removeInstance, removeSingleAttachment } from "../../ActionCreators/AllActionCreators";
import {
    ICandyApiDraftAttachmentsClient,
    ICandyApiDraftTasksClient,
} from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { IChangeSetManager } from "../../Core/ChangeSet/IChangeSetManager";
import { DataDeclarationHelper } from "../../Core/DataDeclaration/DataDeclarationHelper";
import { ModelChunk } from "../../Core/ModelStoring/ModelChunk";
import { ModelHunk } from "../../Core/ModelStoring/ModelHunk";
import { FormStore } from "../../Core/Store/CandyStore";
import { IFormModelAccessor } from "../../Form/IFormModelAccessor";
import { AttachInfo } from "../../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { BindingPath } from "../../Core/CoreTypes";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsolute,
    ModelPathImpl,
} from "../../../../../Common/ModelPath/ModelPath";
import { AttachmentInfoPath } from "../../../../../Common/PathConstants";
import { FileProps } from "../../Core/ModalManager/Modals/Utils/Types";
import { getMask } from "../../../../../Common/ModelPath/ModelPathHelper";
import { SendChangeSetOptions } from "../../Core/ChangeSet/ChangeSetManager";
import { reject } from "../../../../../Common/TypingUtils";

import { BulkFiles, IAttachmentsManager } from "./IAttachmentsManager";
import { addAttachments, removeAttachment, updateAttachment } from "./Store/AttachmentsActions";
import { ConvertTask } from "./Tasks/ConvertTask";
import { TaskStorage } from "./Tasks/TaskStorage";
import { UploadToDriveTask } from "./Tasks/UploadToDriveTask";

export class AttachmentsManager implements IAttachmentsManager {
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly store: FormStore;
    private readonly modelAccessor: IFormModelAccessor;
    private readonly changeSet: IChangeSetManager;
    private readonly candyApiDraftClient: ICandyApiDraftAttachmentsClient & ICandyApiDraftTasksClient;
    private readonly taskStorage: TaskStorage;

    public constructor(
        dataDeclarationHelper: DataDeclarationHelper,
        store: FormStore,
        changeSet: IChangeSetManager,
        candyApiDraftClient: ICandyApiDraftAttachmentsClient & ICandyApiDraftTasksClient,
        modelAccessor: IFormModelAccessor
    ) {
        this.modelAccessor = modelAccessor;
        this.candyApiDraftClient = candyApiDraftClient;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.store = store;
        this.changeSet = changeSet;
        this.taskStorage = new TaskStorage();
    }

    public async addFiles(
        bindingPath: AbsoluteResolvedModelPath,
        files: CustomFile[],
        autoChangeSet: boolean = true,
        gfv?: string
    ): Promise<void> {
        const attachments = await this.createAttachments(bindingPath, files, autoChangeSet, gfv);

        for (const attachment of attachments) {
            if (this.checkAttachmentError(attachment.id)) {
                continue;
            }
            await this.tryUploadToDrive(attachment, bindingPath);
            await this.tryConvertFile(attachment.id, bindingPath);
        }

        this.validateAttachments(attachments.map(attachment => attachment.id));
    }

    public async addBulkFiles(bulkFiles: BulkFiles[]): Promise<void> {
        for (const { bindingPath, files, gfv } of bulkFiles) {
            await this.addFiles(bindingPath, files, false, gfv);
        }

        await this.changeSet.send({ force: true });
    }

    public async addFile(bindingPath: AbsoluteResolvedModelPath, file: CustomFile): Promise<void> {
        const attachment = this.createAttachment(file, bindingPath);
        await this.createSingleAttachment(attachment);

        if (this.checkAttachmentError(attachment.id)) {
            return;
        }
        await this.tryUploadToDrive(attachment, bindingPath);
        await this.tryConvertFile(attachment.id, bindingPath);

        this.validateAttachment(attachment.id);
    }

    public async removeFileByInstance(
        bindingPath: AbsoluteResolvedModelPath,
        instance: string,
        changeSetOptions?: SendChangeSetOptions
    ): Promise<void> {
        const pathToAttachment = bindingPath.joinWith(instance).joinWith(AttachmentInfoPath);
        const id = this.modelAccessor.getValue(pathToAttachment);
        const task = this.taskStorage.getTask(id);
        if (task) {
            task.cancel();
        }

        try {
            if (id != undefined && id !== "") {
                await this.candyApiDraftClient.removeAttachments([id]);
            }
            this.store.dispatch(removeInstance(bindingPath, instance));
            if (id != undefined && id !== "") {
                this.store.dispatch(removeAttachment(id));
            }
            await this.changeSet.send(changeSetOptions);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            reject(`Remove attachment error: attachmentId: "${id}", bindingPath: "${pathToAttachment.toLegacyPath()}"`);
        }
    }

    public async removeFile(
        pathToAttachment: AbsoluteResolvedModelPath,
        changeSetOptions?: SendChangeSetOptions
    ): Promise<void> {
        let id = this.modelAccessor.getValue(pathToAttachment);
        if (id === undefined) {
            const attachInfo = this.store.getState().attachments.byId;
            for (const attachmentId of Object.keys(attachInfo)) {
                if (attachInfo[attachmentId]?.meta.bindingPath === pathToAttachment.toLegacyPath()) {
                    id = attachmentId;
                }
            }
        }
        const task = this.taskStorage.getTask(id);
        if (task) {
            task.cancel();
        }

        try {
            if (id != undefined && id !== "") {
                await this.candyApiDraftClient.removeAttachments([id]);
            }
            this.store.dispatch(removeSingleAttachment(pathToAttachment));
            if (id != undefined && id !== "") {
                this.store.dispatch(removeAttachment(id));
            }
            await this.changeSet.send(changeSetOptions);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            reject(`Remove file error: attachmentId: "${id}", pathToAttachment: "${pathToAttachment.toLegacyPath()}"`);
        }
    }

    public async initTasks(): Promise<void> {
        const state = this.store.getState();
        const attachmentIds = state.attachments.allIds;

        await Promise.all(
            attachmentIds.map((attachmentId: string): Promise<void> => {
                const attachment = state.attachments.byId[attachmentId];
                if (!attachment) {
                    return Promise.resolve();
                }

                const { bindingPath } = attachment.meta;
                return this.tryConvertFile(attachmentId, bindingPath);
            })
        );
    }

    public async openAsDraft(gfv: string, bindingPath: AbsoluteResolvedModelPath): Promise<void> {
        const createResponse = await this.candyApiDraftClient.createDraftAttachment(gfv, bindingPath.toLegacyPath());

        createResponse.meta.pathToAttachment = bindingPath.joinWith(AttachmentInfoPath).toLegacyPath();
        createResponse.meta.bindingPath = bindingPath.toLegacyPath();
        createResponse.meta.progress = 100;

        this.store.dispatch(addAttachments([createResponse]));
        await this.candyApiDraftClient.createOrUpdateAttachments([createResponse]);
        this.setAttachmentToModel(createResponse);
        await this.changeSet.send();
    }

    public async openFufAsDraft(attachInfo: AttachInfo): Promise<void> {
        const createResponse = await this.candyApiDraftClient.openLoadedAttachmentAsDraft(attachInfo.id);
        const newAttach = {
            ...attachInfo,
            nsId: createResponse.nsId,
            draftId: createResponse.draftId,
            meta: {
                ...attachInfo.meta,
                attachmentDraftId: createResponse.meta.attachmentDraftId,
                progress: 100,
            },
        };
        this.store.dispatch(updateAttachment(newAttach.id, newAttach));
    }

    public async loadAsFile(
        loadedFileProps: FileProps,
        gfv: string,
        bindingPath: AbsoluteResolvedModelPath
    ): Promise<void> {
        const attachment = this.createAttachment(
            loadedFileProps.uploadFile,
            bindingPath,
            undefined,
            gfv,
            loadedFileProps.ndsTotalSum
        );
        await this.createSingleAttachment(attachment);

        if (this.checkAttachmentError(attachment.id)) {
            return;
        }
        await this.tryUploadToDrive(attachment, bindingPath);
        await this.tryConvertFile(attachment.id, bindingPath);
    }

    public validateAttachments(attachmentIds?: string[]): void {
        const state = this.store.getState();
        attachmentIds ??= state.attachments.allIds;

        for (const attachmentId of attachmentIds) {
            this.validateAttachment(attachmentId);
        }
    }

    public validateAttachment(attachmentId: string): void {
        if (!attachmentId) {
            return;
        }

        const state = this.store.getState();

        const attachInfo = state.attachments.byId[attachmentId];
        const pathToAttachment = attachInfo?.meta?.pathToAttachment;

        if (
            pathToAttachment != undefined &&
            !attachInfo?.contentUri &&
            (!attachInfo?.file || !this.checkAttachmentError(attachmentId))
        ) {
            this.modelAccessor.setError(pathToAttachment, "Загрузка была прервана. Загрузите ещё раз");
        }
    }

    public async updateAttachment(attachment: AttachInfo): Promise<void> {
        await this.createSingleAttachment(attachment);
    }

    private async createAttachments(
        bindingPath: AbsoluteResolvedModelPath,
        files: CustomFile[],
        autoChangeSet: boolean = true,
        gfv?: string
    ): Promise<AttachInfo[]> {
        const instances = this.modelAccessor.getChildren(bindingPath) ?? [];
        const lastInstances = Number(instances.slice(-1)[0]);
        const attachments: AttachInfo[] = files.map((file, index) => {
            const instance = String(lastInstances + 1 + index || index);
            return this.createAttachment(file, bindingPath, instance, gfv);
        });

        this.store.dispatch(addAttachments(attachments));
        await this.candyApiDraftClient.createOrUpdateAttachments(attachments);
        this.setAttachmentsToModel(bindingPath, instances, attachments);

        if (autoChangeSet) {
            await this.changeSet.send();
        }

        return attachments;
    }

    private async createSingleAttachment(attachment: AttachInfo): Promise<AttachInfo> {
        this.store.dispatch(addAttachments([attachment]));
        await this.candyApiDraftClient.createOrUpdateAttachments([attachment]);
        this.setAttachmentToModel(attachment);
        await this.changeSet.send();
        return attachment;
    }

    private async tryUploadToDrive(
        attachment: AttachInfo,
        bindingPath: BindingPath | AbsoluteResolvedModelPath
    ): Promise<void> {
        if (attachment.contentUri) {
            return;
        }
        const task = this.taskStorage.getTask(attachment.id);
        if (task) {
            await task.taskPromise;
            return;
        }

        const state = this.store.getState();
        const attachInfo = state.attachments.byId[attachment.id];

        if (!attachInfo || !attachInfo.file) {
            this.modelAccessor.setError(bindingPath, "Загрузка была прервана. Загрузите ещё раз");
            return;
        }

        const { fileName } = attachInfo.meta;
        const isConvert = this.dataDeclarationHelper.isFnsConvertFile(bindingPath, fileName);

        const uploadTask = new UploadToDriveTask(
            this.store,
            this.modelAccessor,
            attachment.id,
            bindingPath,
            attachInfo.file,
            isConvert,
            (contentUri: string | undefined) => this.writeResult(contentUri, attachment.id, attachInfo.file)
        );
        this.taskStorage.setTask(attachment.id, uploadTask);
        await uploadTask.taskPromise;
        this.taskStorage.deleteTask(attachment.id);
    }

    private async writeResult(
        contentUri: string | undefined,
        attachmentId: string,
        file: CustomFile | undefined
    ): Promise<void> {
        const attachment = this.store.getState().attachments.byId[attachmentId];

        if (attachment == undefined) {
            // eslint-disable-next-line no-console
            console.error(`AttachInfo with id ${attachmentId} dont exist in uploadToDrive`, file);
            return Promise.resolve();
        }

        const nextAttachment = {
            ...attachment,
            contentUri: contentUri,
        };

        this.store.dispatch(addAttachments([nextAttachment]));
        return await this.candyApiDraftClient.createOrUpdateAttachments([nextAttachment]);
    }

    private async tryConvertFile(
        attachmentId: string,
        bindingPath: BindingPath | AbsoluteResolvedModelPath
    ): Promise<void> {
        const attachInfo = this.store.getState().attachments.byId[attachmentId];

        // Загрузка файла отменена
        if (!attachInfo) {
            return;
        }

        const { fileName, isConverted, pathToAttachment } = attachInfo.meta;
        const modelPath =
            bindingPath instanceof ModelPathImpl
                ? bindingPath.getPathWithoutLastIteration().toLegacyPath()
                : typeof bindingPath === "string"
                  ? getMask(createAbsolute(bindingPath)).getPathWithoutLastIteration().toLegacyPath()
                  : null;

        if (!modelPath) {
            return;
        }

        const isConvert = this.dataDeclarationHelper.isFnsConvertFile(modelPath, fileName);

        if (!isConvert || !attachInfo.contentUri || isConverted || pathToAttachment == undefined) {
            return;
        }

        const task = this.taskStorage.getTask(attachmentId);
        if (task) {
            await task.taskPromise;
            return;
        }

        const convertTask = new ConvertTask(
            this.store,
            this.modelAccessor,
            this.candyApiDraftClient,
            attachmentId,
            pathToAttachment
        );
        this.taskStorage.setTask(attachmentId, convertTask);
        await convertTask.taskPromise;
        this.taskStorage.deleteTask(attachmentId);
    }

    private setAttachmentsToModel(
        bindingPath: BindingPath | AbsoluteModelPath,
        instances: string[],
        attachments: AttachInfo[]
    ): void {
        const modelChunk: ModelChunk = ModelHunk.create();
        ModelHunk.set(modelChunk, bindingPath, {
            children: [...instances, ...attachments.map(({ meta }) => meta.instance ?? "")],
        });

        for (const attachment of attachments) {
            if (attachment.meta.pathToAttachment != undefined) {
                ModelHunk.set(modelChunk, attachment.meta.pathToAttachment, { value: attachment.id });
            }
        }
        this.modelAccessor.setBatch(modelChunk);
    }

    private setAttachmentToModel(attachment: AttachInfo): void {
        if (attachment.meta.pathToAttachment == undefined) {
            return;
        }
        const modelChunk = ModelHunk.createFromValuesBatch({
            [attachment.meta.pathToAttachment]: { value: attachment.id },
        });

        this.modelAccessor.setBatch(modelChunk);
    }

    private checkAttachmentError(attachmentId: string): boolean {
        const state = this.store.getState();
        const attachInfo = state.attachments.byId[attachmentId];
        if (attachInfo == undefined) {
            throw new Error(`Attachment with ${attachmentId} does not exist`);
        }
        const pathToAttachment = attachInfo.meta.pathToAttachment;
        if (pathToAttachment == undefined) {
            return false;
        }

        const error = currentViewModelSelect.pathViewModel(state, pathToAttachment).error ?? [];

        return Boolean(error.length);
    }

    private createAttachment(
        file: CustomFile,
        bindingPath: AbsoluteModelPath,
        instance?: string,
        gfv?: string,
        ndsTotalSum?: string
    ): AttachInfo {
        const id = uuid();
        const bindingPathResolved = instance !== undefined ? bindingPath.joinWith(instance) : bindingPath;
        const attachmentPath = bindingPathResolved.joinWith(AttachmentInfoPath);

        const format = this.dataDeclarationHelper.getTargetFormat(bindingPathResolved.toLegacyPath(), file.name);

        return {
            id: id,
            contentUri: file.contentUri,
            file: file,
            meta: {
                bindingPath: bindingPathResolved.toLegacyPath(),
                pathToAttachment: attachmentPath.toLegacyPath(),
                instance: instance,
                fileName: file.name,
                fileSize: file.size,
                gfv: gfv,
                progress: file.contentUri ? 100 : 0,
                ndsTotalSum: ndsTotalSum ?? file.ndsTotalSum,
                format: format,
            },
        };
    }

    public getAttachmentIdByPath(path: string): string {
        const state = this.store.getState();

        const id = Object.values(state.attachments.byId)?.find(attachment => attachment?.meta.bindingPath === path)?.id;

        if (!id) {
            throw new Error(`attachment id (${id}) not found`);
        }

        return id;
    }
}
